import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { AlertTriangle, Upload, CheckCircle2, Loader2, Layers, X } from 'lucide-react';
import PaidFeaturePaywall from '@/components/PaidFeaturePaywall';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_EMAIL = 'totalproductionsolutions@gmail.com';

/**
 * AquaManagerUploadPanel
 * ------------------------
 * Multi-file AquaManager direct upload — no cap on the number of files or
 * cages. Each file can contribute one cage (per-sheet layout) or many
 * cages (stacked-batch layout). All cages in a batch share species,
 * genetic origin, and FCR (chosen once on the parent form).
 *
 * Step 1 — pick .xls/.xlsx files → POST /api/aquamanager/preview
 * Step 2 — confirmation dialog renders one card per cage (editable name)
 * Step 3 — confirm → POST /api/aquamanager/upload → runs the existing
 *          /api/spreadsheet/upload pipeline once per cage AS-IS
 *
 * Cage naming default: sheet label if meaningful, else filename stem.
 */
export default function AquaManagerUploadPanel({
  recordName,
  species,
  geneticOrigin,
  modelAdjustmentPercent,
  onUploadSuccess,
  isReadOnly,
  costLabel,
}) {
  const { token, user } = useAuth();
  const isAdmin = user?.is_admin === true && user?.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase();
  // Unlimited users (config.UNLIMITED_ACCESS_EMAILS) get the same paid-feature
  // access as admins. Backend gate in dependencies.require_admin_paid_feature
  // stays in sync.
  const hasPaidFeatureAccess = isAdmin || user?.is_unlimited === true;
  const [files, setFiles] = useState([]); // [{file, name}]
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);  // {file_count, cage_count, cages: [...]}
  const [cageEdits, setCageEdits] = useState({}); // key = `${file_index}::${sheet_name}` → edited cage_name
  // Locked-in daily-feed column index per cage. Populated only for cages
  // the backend flagged with `feed_column_ambiguous: true` (RULE-IMP-AQM-003).
  // key = `${file_index}::${sheet_name}` → int column index
  const [feedColumnSelections, setFeedColumnSelections] = useState({});
  const [showConfirm, setShowConfirm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  // Exact-name duplicate confirmation state. When the backend rejects
  // an upload with HTTP 409 `{duplicate_name: true, duplicate_cages}`,
  // we open this dialog instead of showing a red failure toast. The
  // user picks Overwrite / Keep Both / Cancel and we resubmit with
  // the corresponding `duplicate_action`.
  const [duplicatePrompt, setDuplicatePrompt] = useState(null);

  const cageKey = (c) => `${c.file_index}::${c.sheet_name}`;

  const handleFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    const combined = [...files, ...picked.map((f) => ({ file: f, name: f.name }))];
    setFiles(combined);
    setPreview(null);
    setCageEdits({});
    setFeedColumnSelections({});
    setError('');
    // reset native input so re-picking the same files works
    e.target.value = '';
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setPreview(null);
    setCageEdits({});
    setFeedColumnSelections({});
  };

  const readAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const buildFilesPayload = async () =>
    Promise.all(files.map(async (entry) => ({
      file_content: await readAsBase64(entry.file),
      filename: entry.name,
    })));

  const runPreview = async () => {
    if (!files.length) {
      toast.error('Please pick at least one AquaManager Excel file');
      return;
    }
    setPreviewing(true);
    setError('');
    try {
      const filesPayload = await buildFilesPayload();
      const resp = await axios.post(
        `${BACKEND_URL}/api/aquamanager/preview`,
        { files: filesPayload },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPreview(resp.data);
      const seeds = {};
      (resp.data.cages || []).forEach((c) => { seeds[cageKey(c)] = c.cage_name; });
      setCageEdits(seeds);
      setShowConfirm(true);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Preview failed';
      setError(detail);
      toast.error(detail);
    } finally {
      setPreviewing(false);
    }
  };

  const runUpload = async (duplicateAction = null) => {
    if (!files.length || !preview) return;
    setUploading(true);
    setError('');
    try {
      const filesPayload = await buildFilesPayload();
      const cages = (preview.cages || []).map((c) => {
        const key = cageKey(c);
        const entry = {
          file_index: c.file_index,
          sheet_name: c.sheet_name,
          cage_name: (cageEdits[key] || c.cage_name || c.sheet_name).trim(),
        };
        // Only forward feed_column_index for cages the backend flagged
        // as ambiguous — an unnecessary override on an unambiguous cage
        // is harmless but noisier on the wire.
        if (c.feed_column_ambiguous && Number.isInteger(feedColumnSelections[key])) {
          entry.feed_column_index = feedColumnSelections[key];
        }
        return entry;
      });
      const resp = await axios.post(
        `${BACKEND_URL}/api/aquamanager/upload`,
        {
          files: filesPayload,
          cages,
          species,
          genetic_origin: (species === 'Seabass' || species === 'Tilapia' || species === 'Tripletail')
            ? null
            : geneticOrigin,
          temperature_region: 'User uploaded temperatures',
          model_adjustment_percent: modelAdjustmentPercent ?? 100,
          name_prefix: recordName?.trim() || null,
          ...(duplicateAction ? { duplicate_action: duplicateAction } : {}),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const batch = resp.data;
      toast.success(`${batch.cage_count} cage${batch.cage_count > 1 ? 's' : ''} imported and analysed successfully`);
      setShowConfirm(false);
      setFiles([]);
      setPreview(null);
      setCageEdits({});
      if (onUploadSuccess && batch.cages?.length) {
        if (batch.cages.length > 1) {
          toast.info(`Showing "${batch.cages[batch.cages.length - 1].name || ''}". The other ${batch.cages.length - 1} cage(s) are available in Saved Analyses.`);
        }
        onUploadSuccess(batch.cages[batch.cages.length - 1]);
      }
    } catch (err) {
      // Exact-name duplicate is NOT an upload failure — it's a user
      // decision point. Route it to the confirmation dialog instead of
      // the red toast so the user can choose Overwrite / Keep Both.
      const detailObj = err.response?.data?.detail;
      if (err.response?.status === 409 && detailObj && detailObj.duplicate_name) {
        setDuplicatePrompt({
          cages: detailObj.duplicate_cages || [],
          message: detailObj.message || 'A record with this name already exists. Overwrite it with this upload?',
        });
        return; // do not surface as an error
      }
      const detail = typeof detailObj === 'string' ? detailObj : (detailObj?.message || err.message || 'Upload failed');
      setError(detail);
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  const resolveDuplicate = async (action) => {
    setDuplicatePrompt(null);
    await runUpload(action);
  };
  const cancelDuplicate = () => setDuplicatePrompt(null);

  const cages = preview?.cages || [];

  // Ambiguous-feed guard: block the Confirm button until every flagged
  // cage has a daily-feed column picked (RULE-IMP-AQM-003).
  const unresolvedFeedCount = cages.reduce((n, c) => {
    if (!c.feed_column_ambiguous) return n;
    return Number.isInteger(feedColumnSelections[cageKey(c)]) ? n : n + 1;
  }, 0);

  // Non-admin users see a paywall in place of the upload UI. The backend
  // also gates /api/aquamanager/* with a matching 403 so even direct API
  // calls fail with the same message. Unlimited-access users bypass the
  // paywall (kept in sync with backend require_admin_paid_feature).
  if (!hasPaidFeatureAccess) {
    return (
      <div data-testid="aquamanager-upload-panel">
        <PaidFeaturePaywall featureName="AquaManager Direct Upload" tone="sky" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="aquamanager-upload-panel">
      <div
        className="rounded-md border border-sky-300 bg-sky-50 px-3 py-2 text-xs text-sky-900 flex items-start gap-2"
        data-testid="aquamanager-info-notice"
      >
        <Layers className="w-4 h-4 mt-0.5 flex-shrink-0 text-sky-600" />
        <div>
          <p className="font-semibold mb-0.5">
            Direct upload — no cap on the number of cages
          </p>
          <p>
            Upload <strong>any number of AquaManager export files at once</strong>
            {' '}(<span className="font-mono">.xls</span> or <span className="font-mono">.xlsx</span>).
            Each file is one cage; the cage name defaults to the Excel sheet label (or the
            filename if the sheet is generic). All cages in this batch will use the same
            species, genetic origin, and FCR model set above. Harvest average weight is
            auto-calculated from <span className="font-mono">Harvest (Kg) / Harvest (No) × 1000</span>.
          </p>
          <p
            className="mt-2 flex items-start gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] leading-snug text-amber-800"
            data-testid="aqm-species-grouping-note"
          >
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-600" />
            <span>
              <strong>Group uploads by species.</strong> Do not mix multiple species in a single
              upload — every cage in this batch is analysed against the one species / genetic
              origin / FCR model chosen above. Upload each species as its own batch.
            </span>
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <Input
          id="aqmFileUpload"
          type="file"
          accept=".xlsx,.xls"
          multiple
          onChange={handleFiles}
          className="bg-white flex-1"
          data-testid="aqm-file-input"
        />
        <Button
          onClick={runPreview}
          disabled={!files.length || previewing || isReadOnly}
          className="bg-sky-700 hover:bg-sky-800 text-white rounded-full px-6 shadow-md hover:shadow-lg transition-all"
          data-testid="aqm-preview-btn"
        >
          {previewing ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Parsing {files.length} file{files.length > 1 ? 's' : ''}…</>
          ) : (
            <><Upload className="w-4 h-4 mr-2" /> Preview {files.length || ''} AquaManager File{files.length === 1 ? '' : 's'}</>
          )}
        </Button>
      </div>

      {files.length > 0 && (
        <div className="space-y-1.5" data-testid="aqm-selected-files">
          <p className="text-xs text-slate-500">
            {files.length} file{files.length > 1 ? 's' : ''} selected
          </p>
          <ul className="space-y-1">
            {files.map((entry, i) => (
              <li
                key={`${entry.name}-${i}`}
                className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded px-2 py-1 text-xs"
                data-testid={`aqm-selected-file-${i}`}
              >
                <span className="font-mono text-emerald-900 truncate">{entry.name}</span>
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  className="text-emerald-700 hover:text-red-600 ml-2"
                  aria-label={`Remove ${entry.name}`}
                  data-testid={`aqm-remove-file-${i}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && (
        <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <p className="text-sm text-red-700 whitespace-pre-line" data-testid="aqm-error-msg">{error}</p>
        </div>
      )}

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              Confirm AquaManager Import
              {preview?.cage_count > 0 && (
                <span
                  className="text-xs font-normal text-slate-500 bg-slate-100 rounded-full px-2 py-0.5"
                  data-testid="aqm-cage-count-badge"
                >
                  {preview.cage_count} cage{preview.cage_count > 1 ? 's' : ''}
                  {preview.file_count > 1 && ` · ${preview.file_count} files`}
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              Detected format: <strong>AquaManager Export</strong>. All cages below will be
              analysed as <strong>{species}{species === 'Seabream' ? ` · ${geneticOrigin || 'Greek'}` : ''}</strong>
              {' '}using the FCR model configured above. Cage names default to the sheet
              label (or the filename if the sheet name is generic) — edit any field below
              before confirming.
            </DialogDescription>
          </DialogHeader>

          {recordName?.trim() && (
            <p className="text-xs text-slate-500" data-testid="aqm-name-prefix-note">
              Saved record names will be prefixed with <span className="font-mono font-semibold">{recordName.trim()}</span>.
            </p>
          )}

          <div className="space-y-4" data-testid="aqm-preview-cages">
            {cages.map((cage, ci) => (
              <CagePreviewCard
                key={cageKey(cage)}
                cage={cage}
                index={ci}
                editedName={cageEdits[cageKey(cage)] ?? cage.cage_name}
                onRename={(v) => setCageEdits((prev) => ({ ...prev, [cageKey(cage)]: v }))}
                feedColumnSelection={feedColumnSelections[cageKey(cage)]}
                onFeedColumnSelect={(idx) => setFeedColumnSelections((prev) => ({ ...prev, [cageKey(cage)]: idx }))}
              />
            ))}
          </div>

          <DialogFooter className="gap-2 flex-col sm:flex-row">
            <Button variant="outline" onClick={() => setShowConfirm(false)} disabled={uploading} data-testid="aqm-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={() => runUpload()}
              disabled={uploading || isReadOnly || cages.length === 0 || unresolvedFeedCount > 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="aqm-confirm-upload-btn"
            >
              {uploading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running analysis for {cages.length} cage{cages.length > 1 ? 's' : ''}…</>
              ) : unresolvedFeedCount > 0 ? (
                <>Select feed column for {unresolvedFeedCount} cage{unresolvedFeedCount > 1 ? 's' : ''}</>
              ) : (
                <>Confirm &amp; Run Analysis ({cages.length} cage{cages.length > 1 ? 's' : ''}{costLabel || ''})</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Exact-name duplicate confirmation — replaces the red failure
          toast when the backend rejects with HTTP 409
          `{duplicate_name: true, duplicate_cages}`. */}
      <AlertDialog
        open={!!duplicatePrompt}
        onOpenChange={(open) => { if (!open) cancelDuplicate(); }}
      >
        <AlertDialogContent data-testid="aqm-duplicate-name-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {duplicatePrompt?.cages?.length > 1 ? 'Some names already exist' : 'Analysis name already exists'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {duplicatePrompt?.message}
              {duplicatePrompt?.cages?.length > 0 && (
                <ul className="mt-2 text-xs list-disc list-inside text-slate-700 max-h-32 overflow-y-auto">
                  {duplicatePrompt.cages.map((c) => (
                    <li key={c.cage_name}><span className="font-mono">{c.existing_name || c.cage_name}</span></li>
                  ))}
                </ul>
              )}
              <span className="block mt-2 text-xs text-slate-500">
                Overwrite replaces the existing record(s) and invalidates any saved analysis on them.
                Keep Both preserves the existing record(s) and creates new one(s) with a numeric suffix.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={cancelDuplicate}
              data-testid="aqm-duplicate-cancel-btn"
            >
              Cancel
            </AlertDialogCancel>
            <Button
              variant="outline"
              onClick={() => resolveDuplicate('keep_both')}
              data-testid="aqm-duplicate-keep-both-btn"
            >
              Keep Both
            </Button>
            <AlertDialogAction
              onClick={() => resolveDuplicate('overwrite')}
              data-testid="aqm-duplicate-overwrite-btn"
            >
              {duplicatePrompt?.cages?.length > 1 ? 'Overwrite All' : 'Overwrite Existing'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CagePreviewCard({ cage, index, editedName, onRename, feedColumnSelection, onFeedColumnSelect }) {
  const s = cage.summary || {};
  const ambiguous = cage.feed_column_ambiguous === true;
  const candidates = Array.isArray(cage.feed_column_candidates) ? cage.feed_column_candidates : [];
  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-4 space-y-3"
      data-testid={`aqm-cage-card-${index}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
          {cage.filename && (
            <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-mono">
              File: {cage.filename}
            </span>
          )}
          <span className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 font-mono">
            Sheet: {cage.sheet_name}
          </span>
          {cage.warnings?.length > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
              {cage.warnings.length} warning{cage.warnings.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-500" htmlFor={`cage-name-${index}`}>Cage name</label>
          <Input
            id={`cage-name-${index}`}
            value={editedName}
            onChange={(e) => onRename(e.target.value)}
            className="h-7 text-sm w-56"
            data-testid={`aqm-cage-name-input-${index}`}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 text-xs">
        <MiniKpi label="Rows" value={s.rows_imported} />
        <MiniKpi label="Date range" value={s.date_range ? `${s.date_range.start} → ${s.date_range.end}` : '—'} mono small />
        <MiniKpi label="Feed (kg)" value={s.total_feed_kg?.toLocaleString?.()} />
        <MiniKpi label="Mortality" value={s.total_mortality?.toLocaleString?.()} />
        <MiniKpi label="Samples" value={s.routine_sample_points} />
        <MiniKpi label="Harvests" value={s.harvest_events} />
      </div>

      <div className="text-[11px] text-slate-600 bg-slate-50 rounded-md px-3 py-1.5">
        Stocking: <strong>{s.initial_fish_no?.toLocaleString?.()}</strong> fish
        @ <strong>{s.initial_avg_wt_g} g</strong>. Last AquaManager Fish No:{' '}
        <strong className="font-mono">{cage.aquamanager_fish_number?.toLocaleString?.()}</strong>{' '}
        on {cage.aquamanager_fish_number_date}.
      </div>

      {/* Flexible-feed picker — surfaces ONLY when the backend flagged
          2+ plausible feed columns (RULE-IMP-AQM-003). Unambiguous
          cages skip this block entirely. */}
      {ambiguous && (
        <div
          className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 space-y-1.5"
          data-testid={`aqm-feed-picker-${index}`}
        >
          <div className="flex items-start gap-1.5 text-xs text-amber-900">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-600" />
            <div>
              <p className="font-semibold">Multiple feed columns detected</p>
              <p>Which column represents Daily Feed?</p>
            </div>
          </div>
          <select
            className="w-full text-xs rounded border border-amber-300 bg-white px-2 py-1"
            value={Number.isInteger(feedColumnSelection) ? String(feedColumnSelection) : ''}
            onChange={(e) => onFeedColumnSelect(parseInt(e.target.value, 10))}
            data-testid={`aqm-feed-picker-select-${index}`}
          >
            <option value="" disabled>Select the daily-feed column…</option>
            {candidates.map((c) => (
              <option key={c.index} value={String(c.index)}>{c.header}</option>
            ))}
          </select>
          <p className="text-[11px] text-amber-800">
            PFD did not modify the uploaded file — this choice is used only for this upload.
          </p>
        </div>
      )}

      {/* Confidently-resolved feed column — quiet inline confirmation.
          Rendered only when the source column name differs from the
          canonical AquaManager label, so plain "Feed Qty 1" uploads
          stay noise-free. */}
      {!ambiguous && cage.feed_column_selected_header && !/^(feed(\s|$)|feed\s*qty|actual\s*feed|τροφη)/i.test(cage.feed_column_selected_header.trim()) && (
        <p className="text-[11px] text-slate-500" data-testid={`aqm-feed-resolved-${index}`}>
          Daily feed detected in column <span className="font-mono">{cage.feed_column_selected_header}</span>.
        </p>
      )}

      {cage.harvest_events?.length > 0 && (
        <details className="text-xs" data-testid={`aqm-cage-harvest-${index}`}>
          <summary className="cursor-pointer text-slate-600 font-semibold py-1">
            Harvest events ({cage.harvest_events.length})
          </summary>
          <div className="overflow-x-auto mt-1">
            <table className="w-full text-[11px] border-collapse">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left px-2 py-1 border">Date</th>
                  <th className="text-right px-2 py-1 border">Fish</th>
                  <th className="text-right px-2 py-1 border">Biomass (kg)</th>
                  <th className="text-right px-2 py-1 border">Calculated avg (g)</th>
                </tr>
              </thead>
              <tbody>
                {cage.harvest_events.map((ev, i) => (
                  <tr key={i} className="even:bg-slate-50">
                    <td className="px-2 py-1 border font-mono">{ev.date}</td>
                    <td className="px-2 py-1 border text-right">{ev.harvest_no.toLocaleString()}</td>
                    <td className="px-2 py-1 border text-right">{ev.harvest_kg.toLocaleString()}</td>
                    <td className="px-2 py-1 border text-right font-semibold">{ev.avg_weight_g}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {cage.warnings?.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-amber-800 font-semibold py-1">
            Warnings ({cage.warnings.length})
          </summary>
          <ul className="list-disc list-outside pl-5 text-amber-800 mt-1 space-y-0.5">
            {cage.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </details>
      )}

      {cage.rejected_rows?.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-red-800 font-semibold py-1">
            Rejected rows ({cage.rejected_rows.length})
          </summary>
          <ul className="list-disc list-outside pl-5 text-red-800 mt-1 space-y-0.5 max-h-32 overflow-y-auto">
            {cage.rejected_rows.map((r, i) => (
              <li key={i}>Row {r.row}: {r.reason}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function MiniKpi({ label, value, mono, small }) {
  return (
    <div className="rounded-md bg-slate-50 px-2 py-1.5">
      <p className="text-[9px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`font-semibold text-slate-900 ${mono ? 'font-mono' : ''} ${small ? 'text-[11px]' : 'text-sm'}`}>
        {value === null || value === undefined || value === '' ? '—' : value}
      </p>
    </div>
  );
}
